import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Shield, Target, TrendingDown, CheckCircle, Award, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import EnergySavingsCalculator from '../components/EnergySavingsCalculator';

const HomePage = () => {
  const servicesOverview = [
    {
      id: 1,
      title: 'Utility Rate Analysis',
      description: 'Comprehensive review of current utility rates, tariff structures, and billing accuracy to identify optimization opportunities.',
      icon: <BarChart3 size={28} />
    },
    {
      id: 2,
      title: 'Energy Cost Optimization',
      description: 'Strategic recommendations to reduce energy costs through rate optimization, demand management, and operational improvements.',
      icon: <TrendingDown size={28} />
    },
    {
      id: 3,
      title: 'Regulatory & Tariff Review',
      description: 'Analysis of regulatory changes, tariff updates, and rate case proceedings that may impact your organization.',
      icon: <Shield size={28} />
    },
    {
      id: 4,
      title: 'Strategic Energy Advisory',
      description: 'Long-term energy strategy development including procurement planning, sustainability alignment, and risk management.',
      icon: <Target size={28} />
    }
  ];

  const whyChooseUs = [
    {
      id: 1,
      title: 'Independent Analysis',
      description: 'No affiliations with energy suppliers or vendors. Recommendations based solely on your best interests.'
    },
    {
      id: 2,
      title: 'Data-Driven Approach',
      description: 'Every recommendation backed by thorough analysis of your utility data, market conditions, and regulatory environment.'
    },
    {
      id: 3,
      title: 'Practical Solutions',
      description: 'Actionable strategies that deliver measurable results, not theoretical frameworks.'
    },
    {
      id: 4,
      title: 'Industry Expertise',
      description: 'Deep understanding of utility rate structures, regulatory processes, and energy market dynamics.'
    }
  ];

  const trustBadges = [
    { icon: <Award size={24} />, title: '20+ Years', subtitle: 'Combined Experience' },
    { icon: <Users size={24} />, title: '500+', subtitle: 'Clients Served' },
    { icon: <TrendingDown size={24} />, title: '$10M+', subtitle: 'Client Savings' },
    { icon: <Clock size={24} />, title: '48 Hour', subtitle: 'Response Time' }
  ];

  const clientTypes = [
    'Commercial Real Estate',
    'Manufacturing Facilities',
    'Municipal Governments',
    'Healthcare Systems',
    'Educational Institutions',
    'Data Centers'
  ];

  const awards = [
    {
      image: '/award-diamond.png',
      title: 'Diamond Award Winner',
      subtitle: '2026'
    },
    {
      image: '/award-customer-service.png',
      title: 'Top 10 Customer Service',
      subtitle: 'Awarded by Front Runner'
    },
    {
      image: '/award-five-star.png',
      title: 'Five Star Award Energy',
      subtitle: 'Brick, Inc - Jan 2026'
    }
  ];

  return (
    <main className="page-content" data-testid="home-page">
      {/* Calculator Hero Section */}
      <section className="calculator-hero-section">
        <div className="calculator-hero-overlay"></div>
        <div className="container calculator-hero-container">
          <div className="calculator-hero-header">
            <span className="overline" data-testid="hero-overline">Energy Procurement Experts</span>
            <h1 className="calculator-hero-title">Calculate Your Energy Savings</h1>
            <p className="calculator-hero-subtitle">
              Enter your ZIP code and annual usage to see how much you could save on electricity and natural gas costs.
            </p>
          </div>
          <EnergySavingsCalculator />
        </div>
      </section>

      {/* Awards Showcase */}
      <section className="awards-section" data-testid="awards-section">
        <div className="container">
          <div className="awards-header">
            <span className="overline">Award-Winning Service</span>
            <h2>Recognized Industry Excellence</h2>
          </div>
          <div className="awards-grid">
            {awards.map((award, index) => (
              <div key={index} className="award-card" data-testid={`award-card-${index}`}>
                <img src={award.image} alt={award.title} className="award-image" />
                <div className="award-title">{award.title}</div>
                <div className="award-subtitle">{award.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="trust-badges-section">
        <div className="container">
          <div className="trust-badges-grid">
            {trustBadges.map((badge, index) => (
              <div key={index} className="trust-badge-item" data-testid={`trust-badge-${index}`}>
                <div className="trust-badge-icon">{badge.icon}</div>
                <div className="trust-badge-content">
                  <span className="trust-badge-title">{badge.title}</span>
                  <span className="trust-badge-subtitle">{badge.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-light">
        <div className="container">
          <div className="about-hero-grid">
            <div className="about-hero-content">
              <span className="overline">Who We Are</span>
              <h2 className="section-title text-left" style={{marginTop: '0.5rem'}}>Strategic Energy Cost Advisory</h2>
              <p className="about-hero-text">
                Last Mile Advisors helps organizations understand, manage, and reduce utility expenses through data-driven analysis and independent guidance.
              </p>
              <p className="about-hero-text">
                We bring objectivity, expertise, and a commitment to your success. Our team of energy professionals has helped hundreds of organizations optimize their utility costs.
              </p>
              <div className="about-hero-features">
                <div className="about-feature">
                  <CheckCircle size={18} className="about-feature-icon" />
                  <span>Vendor-neutral recommendations</span>
                </div>
                <div className="about-feature">
                  <CheckCircle size={18} className="about-feature-icon" />
                  <span>No hidden fees or commissions</span>
                </div>
                <div className="about-feature">
                  <CheckCircle size={18} className="about-feature-icon" />
                  <span>Guaranteed cost analysis</span>
                </div>
              </div>
              <Link to="/about">
                <Button className="btn-primary" data-testid="learn-more-btn">
                  Learn More About Us
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="about-hero-image">
              <img
                src="https://images.unsplash.com/photo-1694702730868-a34da58c1427?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc1MjUwMDk2fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern glass office building"
                className="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <span className="overline">Our Process</span>
            <h2 className="section-title">What We Do</h2>
            <p className="section-subtitle">
              Expert analysis and strategic guidance to help you take control of your energy costs.
            </p>
          </div>
          <div className="explainer-grid">
            <div className="explainer-item">
              <h3 className="explainer-heading">Understand Your Costs</h3>
              <p className="explainer-text">
                We analyze your utility bills, rate structures, and usage patterns to provide a clear picture of where your money goes and why.
              </p>
            </div>
            <div className="explainer-item">
              <h3 className="explainer-heading">Identify Opportunities</h3>
              <p className="explainer-text">
                Our analysis uncovers rate optimization opportunities, billing errors, and operational improvements that can reduce your costs.
              </p>
            </div>
            <div className="explainer-item">
              <h3 className="explainer-heading">Implement Solutions</h3>
              <p className="explainer-text">
                We guide you through implementing changes, coordinating with utilities, and monitoring results to ensure lasting improvements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section section-services">
        <div className="section-services-overlay"></div>
        <div className="container section-services-content">
          <div className="section-header">
            <span className="overline">Services</span>
            <h2 className="section-title-light">Our Services</h2>
            <p className="section-subtitle-light">
              Comprehensive energy and utility advisory services tailored to your organization's needs.
            </p>
          </div>
          <div className="services-grid">
            {servicesOverview.map((service) => (
              <Card key={service.id} className="service-card" data-testid={`service-card-${service.id}`}>
                <CardContent className="service-card-content">
                  <div className="service-icon">
                    {service.icon}
                  </div>
                  <h3 className="service-card-title">{service.title}</h3>
                  <p className="service-card-description">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="section-cta-inline">
            <Link to="/services">
              <Button variant="outline" className="btn-outline-light" data-testid="view-services-btn">
                View All Services
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Last Mile Advisors */}
      <section className="section section-light">
        <div className="container">
          <div className="why-us-hero-grid">
            <div className="why-us-image-container">
              <img
                src="https://images.unsplash.com/photo-1714330859170-695ea7edd2ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMHN1bnNldCUyMGVuZXJneXxlbnwwfHx8fDE3NzUyNTAwOTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Solar panels at sunset"
                className="why-us-image"
              />
            </div>
            <div className="why-us-content">
              <span className="overline">Why Choose Us</span>
              <h2 className="section-title text-left" style={{marginTop: '0.5rem'}}>Why Last Mile Advisors</h2>
              <p className="why-us-intro">
                We bring objectivity, expertise, and a commitment to your success.
              </p>
              <div className="why-us-list">
                {whyChooseUs.map((item) => (
                  <div key={item.id} className="why-us-list-item">
                    <CheckCircle size={18} className="why-us-check" />
                    <div>
                      <h4 className="why-us-item-title">{item.title}</h4>
                      <p className="why-us-item-desc">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <span className="overline">Industries</span>
            <h2 className="section-title">Who We Serve</h2>
            <p className="section-subtitle">
              We work with organizations across industries that have significant energy and utility expenses.
            </p>
          </div>
          <div className="clients-grid">
            {clientTypes.map((client, index) => (
              <div key={index} className="client-item" data-testid={`client-type-${index}`}>
                <span className="client-text">{client}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta-hero">
        <div className="section-cta-overlay"></div>
        <div className="container section-cta-content">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Take Control of Your Energy Costs?</h2>
            <p className="cta-subtitle">
              Schedule a consultation to discuss your organization's energy situation and learn how we can help.
            </p>
            <Link to="/contact">
              <Button className="btn-primary btn-lg" data-testid="cta-consultation-btn">
                Request a Consultation
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
